import { useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { AxiosError } from 'axios'
import { useMutation } from '@tanstack/react-query'
import { useSnackbar } from '@ui/snackbar'
import { useModelStore } from '@editorStores'
import { useBlockScene, useResultsInvalidation } from '@editorHooks'
import { useModelInvalidation } from '@structuralPlanningHooks'
import { updateWall as updateWallRequest } from '@mutations'
import { buildErrorMessage } from 'src/constants/errors'

/**
 * Auto-saves a wall's geometry after it is moved or resized with `WallEdit` in the IFC importer.
 * `WallEdit.editStop` writes the new geometry to the store (flagging it `is_translated`/`is_updated`)
 * before calling `onEditStop`; passing the wall's guid here then persists it via `updateWall`, so
 * scene edits behave like the auto-create-on-draw flow and never leave unsaved state in the store.
 * A failed save is rolled back by refetching the model (server geometry wins).
 */
const useIFCImportWallEditAutoSave = (): ((wallGuid: string) => void) => {
  const { projectId }: { projectId?: string } = useParams()
  const { enqueueSnackbar } = useSnackbar()

  const invalidateModel = useModelInvalidation()
  const invalidateResults = useResultsInvalidation()

  const { mutate: saveWall, isLoading } = useMutation(
    (wall: ShapeObject) => {
      const [start, end] = wall.shape.points
      const newPosition: NewWallPosition = {
        new_start: { x: start.x, y: start.y },
        new_end: { x: end.x, y: end.y },
        placement: wall.placement || 'External',
      }
      return updateWallRequest.request(projectId as string, wall.guid, newPosition)
    },
    {
      mutationKey: ['updateWall', projectId],
      meta: {
        title: 'Wand ändern',
        description: 'Speichert die geänderte Wandgeometrie im Planarmodell.',
      },
      onSuccess: async () => {
        await invalidateModel(projectId as string)
        invalidateResults(projectId as string)
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Ändern der Wand'), {
          variant: 'error',
        })
        // Roll back the unsaved scene edit by restoring the server geometry.
        invalidateModel(projectId as string)
      },
    },
  )

  // Block the scene while the edited geometry is being saved so no other interaction races it.
  useBlockScene(isLoading)

  return useCallback(
    (wallGuid: string) => {
      const wall = useModelStore.getState().model.walls.find(current => current.guid === wallGuid)
      if (!wall) return
      // Only persist an actual geometry edit; e.g. an escape-cancelled drag leaves no flags set.
      if (!wall.is_translated && !wall.is_updated) return

      saveWall(wall)
    },
    [saveWall],
  )
}

export default useIFCImportWallEditAutoSave
